import { useMutation } from "@tanstack/react-query";
import { useLingui } from "@lingui/react";
import { toast } from "sonner";
import { formatLocalDate } from "@/utils/dateUtils";

/**
 * Download of the attendance and care ratio inspection report (#1501): one German
 * document per location and period for the childcare supervisory authority, as a PDF or
 * as an Excel workbook. Both formats render the same computed report.
 *
 * The endpoints stream a binary file rather than JSON, so they use a raw `fetch` with a
 * bearer token instead of the generated OpenAPI client, like the settling-in PDF
 * downloads.
 */

/** The document format: the route suffix of the report endpoint. */
export type AttendanceReportFormat = "pdf" | "xlsx";

export interface DownloadAttendanceReportParams {
  childcareId: string;
  from: Date;
  to: Date;
}

/**
 * A failed download carrying the HTTP status and the server's JSON body, so the
 * dialog's `errorToastText` can translate a known `code` and show a 400 reason
 * (missing, unparseable, inverted or too long range) in place of a 5xx text.
 */
export class AttendanceReportDownloadError extends Error {
  readonly status: number;
  readonly body?: unknown;

  constructor(message: string, status: number, body?: unknown) {
    super(message);
    this.name = "AttendanceReportDownloadError";
    this.status = status;
    this.body = body;
  }
}

interface DownloadedReport {
  blob: Blob;
  filename: string;
}

const FALLBACK_FILENAMES: Record<AttendanceReportFormat, string> = {
  pdf: "anwesenheitsnachweis.pdf",
  xlsx: "anwesenheitsnachweis.xlsx",
};

async function downloadAttendanceReport(
  format: AttendanceReportFormat,
  { childcareId, from, to }: DownloadAttendanceReportParams,
  fallbackMessage: (status: number) => string
): Promise<DownloadedReport> {
  const { getAuthToken } = await import("@/api/auth-helper");
  const token = await getAuthToken();
  const baseUrl = import.meta.env.VITE_API_BASE || "";
  // formatLocalDate keeps the LOCAL calendar day (yyyy-MM-dd); toISOString() would roll
  // it back a day in any zone ahead of UTC.
  const query = `from=${formatLocalDate(from)}&to=${formatLocalDate(to)}&lang=de`;

  const response = await fetch(
    `${baseUrl}/api/v1/childcares/${encodeURIComponent(childcareId)}/attendance-report/${format}?${query}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    const message =
      body && typeof body === "object" && "message" in body && typeof body.message === "string" && body.message
        ? body.message
        : fallbackMessage(response.status);
    throw new AttendanceReportDownloadError(
      message,
      response.status,
      body && typeof body === "object" ? body : undefined
    );
  }

  const blob = await response.blob();
  const disposition = response.headers.get("Content-Disposition");
  const filenameMatch = disposition?.match(/filename="?([^"]+)"?/);
  const filename = filenameMatch?.[1] || FALLBACK_FILENAMES[format];

  return { blob, filename };
}

/** Saves the blob under its filename via a transient anchor click. */
function saveBlob({ blob, filename }: DownloadedReport): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

/**
 * Downloads the inspection report in one format. There is deliberately no error toast:
 * the export dialog renders the failure inline.
 */
function useDownloadAttendanceReport(format: AttendanceReportFormat) {
  const { i18n } = useLingui();

  return useMutation<DownloadedReport, AttendanceReportDownloadError, DownloadAttendanceReportParams>({
    mutationFn: (params) =>
      downloadAttendanceReport(format, params, (status) =>
        i18n._({
          id: "coverage.attendanceReport.httpError",
          message: "Download failed (HTTP {status})",
          values: { status },
        })
      ),
    onSuccess: (result) => {
      saveBlob(result);
      toast.success(
        format === "xlsx"
          ? i18n._({ id: "coverage.attendanceReport.successExcel", message: "Excel file downloaded" })
          : i18n._({ id: "coverage.attendanceReport.success", message: "PDF downloaded" })
      );
    },
  });
}

/** Downloads the inspection report PDF. */
export function useDownloadAttendanceReportPDF() {
  return useDownloadAttendanceReport("pdf");
}

/** Downloads the inspection report as an Excel workbook (.xlsx). */
export function useDownloadAttendanceReportXLSX() {
  return useDownloadAttendanceReport("xlsx");
}
